import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { subMonths, addMonths } from 'date-fns';
import { SharedDateService } from '../../../../../shared/services/shared-date.service';
import { SidebarService } from '../../../../../shared/services/sidebar.service';
import { TranslateService } from '@ngx-translate/core';
import { MapService } from '../../../../../shared/services/map.service';
import { AuthService } from '../../../../../shared/services/auth.service';
import { GlobalVars } from '../../../../../shared/global-vars';
import { GroupRole } from '../../../../../shared/models/group-role';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit {
  selectedDate: Date = new Date();
  exactSelectedDate: Date = new Date();
  visibleMonths: { name: string, value: number, year: number }[] = [];
  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();
  selectedMonth: { name: string, value: number, year: number };
  minSelectableDate: Date = new Date(2022, 8);
  maxSelectableDate: Date = new Date(this.currentYear, this.currentMonth);
  currentZoomLevel: number = 0;
  isLoggedIn: boolean = false;
  userGroups: GroupRole[] = [];
  isPremium: boolean = false;
  today: Date = new Date();

  disablePast: boolean = false;
  disableFuture: boolean = false;

  isDetailsOpen: boolean = false;

  months = [
    { name: 'MAP.MONTHS.JANUARY', value: 0 },
    { name: 'MAP.MONTHS.FEBRUARY', value: 1 },
    { name: 'MAP.MONTHS.MARCH', value: 2 },
    { name: 'MAP.MONTHS.APRIL', value: 3 },
    { name: 'MAP.MONTHS.MAY', value: 4 },
    { name: 'MAP.MONTHS.JUNE', value: 5 },
    { name: 'MAP.MONTHS.JULY', value: 6 },
    { name: 'MAP.MONTHS.AUGUST', value: 7 },
    { name: 'MAP.MONTHS.SEPTEMBER', value: 8 },
    { name: 'MAP.MONTHS.OCTOBER', value: 9 },
    { name: 'MAP.MONTHS.NOVEMBER', value: 10 },
    { name: 'MAP.MONTHS.DECEMBER', value: 11 }
  ];

  constructor(
    private cdRef: ChangeDetectorRef,
    private sharedDateService: SharedDateService,
    private sidebarService: SidebarService,
    private translate: TranslateService,
    private mapService: MapService,
    private authService: AuthService) {
      this.selectedMonth = {
        name: this.months[this.currentMonth].name,
        value: this.currentMonth,
        year: this.currentYear
      };
      this.sidebarService.sidebarDetailsSource$.subscribe((res)=> {
        this.isDetailsOpen = res;
      });
  }

  ngOnInit() {
    this.generateVisibleMonths();

    this.authService.getUserInfo().subscribe( res => {
      if(res?.groupeUtilisateur)
      this.userGroups = res?.groupeUtilisateur;
      if(this.userGroups)
      this.isPremium = this.userGroups.some(groupRole => groupRole.groupe.nom !== "Public");
    });

    this.sharedDateService.resetDate$.subscribe(res => {
      if(this.today.getMonth() < this.selectedDate.getMonth() || this.today.getFullYear() < this.selectedDate.getFullYear()){
        this.selectedMonth = {
          name: this.months[this.today.getMonth()].name,
          value: this.today.getMonth(),
          year: this.today.getFullYear()
        };
        this.onMonthSelect({ value: this.selectedMonth.value, year: this.selectedMonth.year });
      }
    });

    this.translate.onLangChange.subscribe(() => {
      const currentSelectedDate = this.selectedDate;
      this.selectedDate = new Date();
      setTimeout(() => {
        this.selectedDate = currentSelectedDate;
      }, 0);
    });

    this.mapService.zoomLevel$.subscribe((zoomLevel) => {
      this.currentZoomLevel = zoomLevel;
      this.isLoggedIn = this.authService.isUserLoggedIn();
      this.updateMaxSelectableDate();

    });

    this.onMonthSelect({ value: this.selectedMonth.value, year: this.selectedMonth.year });

    this.disableFuture = new Date(this.selectedDate) >= this.maxSelectableDate ;
    this.disablePast = new Date(this.selectedDate) <= this.minSelectableDate ;
  }

  changeMonth(direction: number) {
    if (direction === -1) {
      this.selectedDate = subMonths(this.selectedDate, 1);
    } else if (direction === 1) {
      this.selectedDate = addMonths(this.selectedDate, 1);
    }

    // Mise à jour du service avec la vraie date exacte
    this.sharedDateService.updateSelectedDate(this.selectedDate);
    this.sidebarService.setSidebarStatus(false);
    this.disableFuture = new Date(this.selectedDate) >= this.maxSelectableDate ;
    this.disablePast = new Date(this.selectedDate) <= this.minSelectableDate ;
    this.cdRef.markForCheck();
  }

  onCalendarSelect(event: any): void {
    const selectedDate = new Date(event);
    this.exactSelectedDate = selectedDate;

    const selected = this.visibleMonths.find(month => month.value === selectedDate.getMonth() && month.year === selectedDate.getFullYear());

    if (selected) {
      this.selectedMonth = selected;
    } else {
      this.selectedMonth = {
        name: this.months[selectedDate.getMonth()].name,
        value: selectedDate.getMonth(),
        year: selectedDate.getFullYear()
      };
    }

    // Fixe selectedDate au 1er jour du mois pour le calendrier
    this.selectedDate = this.getFirstDayOfMonth(this.selectedMonth.year, this.selectedMonth.value);

    //maj des boutons précédent et suivant
    this.disableFuture = new Date(this.selectedDate) >= this.maxSelectableDate ;
    this.disablePast = new Date(this.selectedDate) <= this.minSelectableDate ;

    // Mise à jour du service avec la vraie date exacte
    this.sharedDateService.updateSelectedDate(this.exactSelectedDate);
    this.sidebarService.setSidebarStatus(false);
    this.cdRef.markForCheck();
  }

  generateVisibleMonths(): void {
    const months = [];
    const start = new Date(this.currentYear - 1, this.currentMonth);
    const end = new Date(this.currentYear, this.currentMonth + 3);

    for (let d = start; d <= end; d.setMonth(d.getMonth() + 1)) {

      months.push({
        name: `${this.months[d.getMonth()].name} ${d.getFullYear()}`,
        value: d.getMonth(),
        year: d.getFullYear()
      });
    }

    this.visibleMonths = months;
  }

  getFirstDayOfMonth(year: number, month: number): Date {
    return new Date(year, month, 1);
  }

  updateMaxSelectableDate(): void {
    let futureMonthsAllowed = 0;
    if (this.currentZoomLevel >= GlobalVars.wfsSimplifiedMaxZoomAndSensors) {
      futureMonthsAllowed = this.isLoggedIn && this.isPremium ? 3 : 1;
    }
    this.maxSelectableDate = new Date(this.currentYear, this.currentMonth + futureMonthsAllowed);
    this.cdRef.markForCheck();
  }

  onMonthSelect(event: any): void {
    const selected = this.visibleMonths.find(month => month.value === event.value && month.year === event.year);
    if (selected) {
      this.selectedMonth = selected;

      // Utilisation du premier jour du mois pour la sélection du calendrier
      this.selectedDate = this.getFirstDayOfMonth(event.year, event.value);

      // Garder la date exacte dans la nouvelle sélection
      this.exactSelectedDate = this.getValidDateForMonth(event.year, event.value);

      // Transmettre la date exacte au service
      this.sharedDateService.updateSelectedDate(this.exactSelectedDate);
      this.sidebarService.setSidebarStatus(false);
    }
    this.cdRef.markForCheck();
  }

  getValidDateForMonth(year: number, month: number): Date {
    const currentDay = this.exactSelectedDate.getDate();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    const validDay = Math.min(currentDay, lastDayOfMonth);

    return new Date(year, month, validDay);
  }
}
